import React, { useState, useEffect, useRef } from "react";
import { PROFILE, UPDATE_AUTH } from "../../constant";
import { GET, PUT } from "../../constant/apiMethods";
import { connect } from "react-redux";
import Labels from "../../services/apiLabels";
import config from "../../config";
import profileImage from "../../assets/images/no_user.png";
import { getAuthUserId } from "../../services/localStorage";
import {
  isEmail,
  isFilled,
} from "../../utils/formValidation";
import {
  buildAuthProfilePatch,
  resolveMediaUrl,
  withCacheBust,
} from "../../../utils/userIdentity";
import { UPDATE_AUTH as CLIENT_UPDATE_AUTH } from "../../../constant";
import clientStore from "../../../store";

export const API_ENDPOINT = (label) => {
  return `${config.baseUrl}${label}`;
};

function authImageValue(raw) {
  const resolved = resolveMediaUrl(raw) || (raw ? String(raw) : "");
  if (!resolved) return "";
  if (resolved.startsWith("data:") || resolved.startsWith("blob:")) {
    return resolved;
  }
  return withCacheBust(resolved);
}

function syncAvatarToMenus(form, imagePath, updateAuth) {
  const patch = buildAuthProfilePatch(form, imagePath || undefined);
  if (!patch.image && !patch.firstname && !patch.lastname) return;
  updateAuth(patch);
  try {
    clientStore.store.dispatch({
      type: CLIENT_UPDATE_AUTH,
      payload: patch,
    });
  } catch (e) {
    /* ignore mirror failures */
  }
}

function Profile({ getprofile, updateprofile, updateAuth, data, loading }) {
  const [formData, setFormData] = useState({});
  const [imageUrl, setImageUrl] = useState(null);
  const [file, setFile] = useState(null);
  const [image, setImage] = useState("");
  const [existingImage, setExistingImage] = useState("");
  const [county, setCounty] = useState("");
  const [country, setCountry] = useState("");
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [saving, setSaving] = useState(false);
  const pendingSave = useRef(false);
  const pendingSaveMeta = useRef(null);
  const awaitServerAvatar = useRef(false);
  const ref = useRef();
  const id = getAuthUserId();
  const isLoading = Boolean(loading) && !data?.user;

  const markTouched = (key) => {
    setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  };

  const updateData = (e) => {
    markTouched(e.target.name);
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const fieldErrors = (() => {
    const errors = {};
    if (!isFilled(formData.first_name)) errors.first_name = "Required";
    if (!isFilled(formData.last_name)) errors.last_name = "Required";
    if (!isFilled(formData.username)) errors.username = "Required";
    if (!isFilled(formData.company_name)) errors.company_name = "Required";
    if (!isEmail(formData.email)) errors.email = "Enter a valid email";
    if (!isFilled(formData.mobile_number)) errors.mobile_number = "Required";
    if (!isFilled(formData.address)) errors.address = "Required";
    return errors;
  })();
  const isFormValid = Object.keys(fieldErrors).length === 0;
  const showError = (key) =>
    Boolean((submitAttempted || touched[key]) && fieldErrors[key]);

  const updatehandler = (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (!isFormValid || saving) return;
    const postData = {
      image: image || existingImage,
      first_name: String(formData.first_name || "").trim(),
      last_name: String(formData.last_name || "").trim(),
      username: String(formData.username || "").trim(),
      company_name: String(formData.company_name || "").trim(),
      email: String(formData.email || "").trim(),
      mobile_number: String(formData.mobile_number || "").trim(),
      address: String(formData.address || "").trim(),
      city: String(formData.city || "").trim(),
      county: county,
      country: country,
      postcode: String(formData.postcode || "").trim(),
      garagecode: String(formData.garagecode || "").trim(),
      type: formData.type,
      client_type: formData.client_type,
    };
    pendingSave.current = true;
    pendingSaveMeta.current = {
      form: { ...formData, ...postData },
      image: postData.image || "",
    };
    setSaving(true);
    updateprofile(id, postData);
  };

  useEffect(() => {
    if (!data) return;
    // Prefer nested user from GET; fall back to flat PUT payload shape.
    const user =
      data.user ||
      (data.first_name != null || data.last_name != null ? data : null) ||
      {};
    const resolved = resolveMediaUrl(user.image) || "";
    if (resolved) {
      setExistingImage(resolved);
    } else if (!image && !imageUrl) {
      setExistingImage("");
    }
    setCounty(user.county ?? "");
    setCountry(user.country ?? "");
    setFormData({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      username: user.username || "",
      company_name: user.company_name || "",
      email: user.email || "",
      mobile_number: user.mobile_number || "",
      address: user.address || "",
      city: user.city || "",
      postcode: user.postcode || "",
      garagecode: user.garagecode || "",
      type: user.type || "",
      client_type: user.client_type || "",
    });

    // Only swap menus to the server URL after a full GET refetch (not the PUT payload).
    const isFullGet =
      Boolean(data.user) &&
      (Array.isArray(data.county) || Array.isArray(data.country));
    if (awaitServerAvatar.current && isFullGet && resolved) {
      awaitServerAvatar.current = false;
      syncAvatarToMenus(
        {
          first_name: user.first_name,
          last_name: user.last_name,
        },
        authImageValue(resolved),
        updateAuth
      );
      setImage("");
      setImageUrl(null);
      setFile(null);
    }
  }, [data, updateAuth]);

  useEffect(() => {
    getprofile(id);
  }, []);

  useEffect(() => {
    if (!pendingSave.current) return;
    if (loading) return;
    pendingSave.current = false;
    setSaving(false);

    const meta = pendingSaveMeta.current;
    pendingSaveMeta.current = null;
    if (meta) {
      const nextImage = authImageValue(meta.image);
      syncAvatarToMenus(meta.form, nextImage, updateAuth);
      awaitServerAvatar.current = Boolean(meta.image);
    }

    getprofile(id);
  }, [loading, id, getprofile, updateAuth]);

  const readURL = (file) => {
    return new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onload = (e) => res(e.target.result);
      reader.onerror = (e) => rej(e);
      reader.readAsDataURL(file);
    });
  };

  async function file_chosen(e) {
    const chosen = e.target.files?.[0];
    if (!chosen) return;
    try {
      setFile(chosen);
      const url = await readURL(chosen);
      setImage(url);
    } catch (err) {
      setFile(null);
    }
  }

  const handleRef = (e) => {
    e.preventDefault();
    ref.current.click();
  };

  useEffect(() => {
    if (file) {
      setImageUrl(URL.createObjectURL(file));
    }
  }, [file]);

  if (isLoading) {
    return (
      <div className="dd-empty" aria-busy="true">
        <strong>Loading profile…</strong>
        <p>Fetching your account details from the dealer desk.</p>
      </div>
    );
  }

  return (
    <div className="dd-section">
      <form className="dd-form" onSubmit={(e) => updatehandler(e)} noValidate>
        <div className="dd-avatar-block">
          <button
            type="button"
            onClick={handleRef}
            className="dd-btn dd-btn--ghost"
            style={{ padding: 4 }}
          >
            <img
              src={imageUrl || existingImage || profileImage}
              alt=""
              onError={(e) => {
                if (e.currentTarget.src !== profileImage) {
                  e.currentTarget.src = profileImage;
                }
              }}
              style={{
                width: "4.5rem",
                height: "4.5rem",
                borderRadius: "999px",
                objectFit: "cover",
              }}
            />
          </button>
          <div>
            <strong>Profile photo</strong>
            <p style={{ margin: 0, color: "#64748b", fontSize: "0.85rem" }}>
              {file === null ? "No new file chosen" : "1 file selected"}
            </p>
          </div>
          <input
            onChange={(e) => file_chosen(e)}
            ref={ref}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
          />
        </div>
        <div className="dd-form-grid">
          {[
            ["first_name", "name-f", "First Name *", "Enter your first name", "text"],
            ["last_name", "name-l", "Last name *", "Enter your last name", "text"],
            ["username", "username", "User name *", "username", "text"],
            ["company_name", "company_name", "Company Name *", "Enter your company name", "text"],
            ["email", "email", "Email *", "Enter your email.", "email"],
            ["mobile_number", "mobile_number", "Phone No *", "Enter your phone no.", "tel"],
            ["address", "address", "Address *", "Locality/House/Street no.", "text"],
          ].map(([name, id, label, placeholder, type]) => (
            <div
              key={name}
              className={`form-group dd-field${showError(name) ? " is-invalid" : ""}`}
            >
              <label htmlFor={id}>{label}</label>
              <input
                type={type}
                className="form-control"
                name={name}
                id={id}
                placeholder={placeholder}
                onChange={updateData}
                onBlur={() => markTouched(name)}
                value={formData?.[name] || ""}
              />
              {showError(name) ? (
                <p className="dd-password__error">{fieldErrors[name]}</p>
              ) : null}
            </div>
          ))}
          <div className="form-group dd-field">
            <label htmlFor="county">County</label>
            <select
              className="form-control p-1 "
              onChange={(e) => setCounty(e.target.value)}
              name="county"
              value={county}
            >
              <option value="">Select county</option>
              {Array.isArray(data?.county)
                ? data.county.map((items) => (
                    <option key={items?.id} value={items.id}>
                      {items?.name}
                    </option>
                  ))
                : null}
            </select>
          </div>
          <div className="form-group dd-field">
            <label htmlFor="country">Country</label>
            <select
              className="form-control p-1 "
              onChange={(e) => setCountry(e.target.value)}
              name="country"
              value={country}
            >
              <option value="">Select country</option>
              {Array.isArray(data?.country)
                ? data.country.map((items) => (
                    <option key={items?.id} value={items.id}>
                      {items?.name}
                    </option>
                  ))
                : null}
            </select>
          </div>
          <div
            className={`form-group dd-field${
              showError("postcode") ? " is-invalid" : ""
            }`}
          >
            <label htmlFor="postcode">Post Code</label>
            <input
              type="text"
              className="form-control"
              name="postcode"
              id="postcode"
              placeholder="Enter your post code."
              onChange={updateData}
              onBlur={() => markTouched("postcode")}
              value={formData?.postcode || ""}
            />
            {showError("postcode") ? (
              <p className="dd-password__error">{fieldErrors.postcode}</p>
            ) : null}
          </div>
          <div
            className={`form-group dd-field${
              showError("garagecode") ? " is-invalid" : ""
            }`}
          >
            <label htmlFor="garagecode">Garage Code</label>
            <input
              type="text"
              className="form-control"
              name="garagecode"
              id="garagecode"
              placeholder="Enter your garage code."
              onChange={updateData}
              onBlur={() => markTouched("garagecode")}
              value={formData?.garagecode || ""}
            />
            {showError("garagecode") ? (
              <p className="dd-password__error">{fieldErrors.garagecode}</p>
            ) : null}
          </div>
          <div className="form-group dd-field">
            <label htmlFor="type">Business Type</label>
            <select
              className="form-control p-1 "
              onChange={updateData}
              name="type"
              value={formData?.type}
            >
              <option value="1">Franchise Dealer</option>
              <option value="2">Independent Dealer</option>
              <option value="3">Lease</option>
              <option value="4">Trade</option>
              <option value="5">Other</option>
            </select>
          </div>
          <div className="form-group dd-field">
            <label htmlFor="client_type">Next Gear Capital Client</label>
            <select
              className="form-control p-1 "
              onChange={updateData}
              name="client_type"
              value={formData?.client_type}
            >
              <option value="1">Yes</option>
              <option value="2">No</option>
            </select>
          </div>
        </div>
        <div className="dd-btn-row">
          <button
            className="dd-btn dd-btn--primary btn_contacta"
            type="submit"
            disabled={!isFormValid || saving}
          >
            {saving ? "Saving…" : "Save profile"}
          </button>
        </div>
      </form>
    </div>
  );
}

const mapStateToProps = (state) => ({
  user_id: state.auth.id,
  data: state.profile.data,
  loading: state.profile.loading,
});

const mapDispatchToProps = (dispatch) => ({
  getprofile: (id) =>
    dispatch({
      type: PROFILE,
      payload: {
        type: PROFILE,
        label: `${Labels.getupdateprofile}${id}`,
        method: GET,
        hideOverlayLoader: true,
        hideSuccessToaster: true,
      },
    }),
  updateprofile: (id, postData) =>
    dispatch({
      type: PROFILE,
      payload: {
        type: PROFILE,
        label: `${Labels.getupdateprofile}${id}/`,
        method: PUT,
        hideOverlayLoader: true,
        data: postData,
      },
    }),
  updateAuth: (payload) =>
    dispatch({
      type: UPDATE_AUTH,
      payload: { ...(payload || {}) },
    }),
});
export default connect(mapStateToProps, mapDispatchToProps)(Profile);
